// Tipos e helpers do almoxarifado da indústria (INDUSTRIA.md, onda 1).
// O saldo aqui é sempre por dono: o mesmo frasco de dois clientes são dois
// saldos, nunca somam.
import { api } from './api'

export type MaterialKind = 'componente' | 'conteudo' | 'embalagem_envio'
export type MaterialUnit = 'UN' | 'CX' | 'RL' | 'M' | 'MIL' | 'KG' | 'L' | 'PC'
export type EntryMode = 'remessa' | 'compra_ws' | 'compra_por_conta'
export type MovementType = 'in' | 'out' | 'adjustment' | 'loss' | 'return_to_owner'
export type VolumeStatus = 'fechado' | 'aberto' | 'consumido' | 'quarentena'

export const KIND_LABEL: Record<MaterialKind, string> = {
  componente: 'Componente',
  conteudo: 'Conteúdo',
  embalagem_envio: 'Embalagem de envio',
}

export const ENTRY_MODE_LABEL: Record<EntryMode, string> = {
  remessa: 'Remessa do cliente',
  compra_ws: 'Compra da WS',
  compra_por_conta: 'Compra por conta do cliente',
}

export const ENTRY_MODE_HINT: Record<EntryMode, string> = {
  remessa: 'A marca manda o material. Continua sendo dela — a WS só guarda e consome.',
  compra_ws: 'A WS compra para si. Sem dono: o material é da casa.',
  compra_por_conta: 'A WS compra, o cliente paga. O material nasce já sendo dele.',
}

export const MOVEMENT_LABEL: Record<MovementType, string> = {
  in: 'Entrada',
  out: 'Saída',
  adjustment: 'Ajuste',
  loss: 'Perda',
  return_to_owner: 'Devolução ao dono',
}

export const UNITS: MaterialUnit[] = ['UN', 'CX', 'RL', 'M', 'MIL', 'KG', 'L', 'PC']

export type IndustryTenant = { id: string; name: string; active: boolean }

export type Supplier = {
  id: string; name: string; cnpj: string | null; email: string | null
  phone: string | null; leadTimeDays: number | null; notes: string | null; active: boolean
}

export type MaterialRow = {
  id: string; code: string; name: string; kind: MaterialKind; unit: MaterialUnit
  ownerTenantId: string | null; ownerName: string | null
  lotControlled: boolean; unitsPerPack: number | null; minQty: number
  avgCost?: string | null; photoMediaId: string | null
  productSku: string | null; supplierName?: string | null
  active: boolean; quantity: number; belowMin: boolean
  // Cobertura em dias (INSUMOS.md §4): saldo ÷ consumo médio dos últimos 30 dias.
  // null quando não houve saída no período — sem consumo não há previsão.
  dailyUse: number; coverageDays: number | null
}

export type MaterialLot = {
  id: string; lotNumber: string; supplierLot: string | null; entryMode: EntryMode
  ownerTenantId: string | null; ownerName: string | null; docNumber: string | null
  unitCost?: string | null; expiryDate: string | null; qtyReceived: number
  status: string; receivedAt: string; supplierName?: string | null; quantity: number
}

export type MaterialVolume = {
  id: string; code: string; lotId: string | null; lotNumber: string | null
  ownerName: string | null; locationCode: string | null
  qtyInitial: number; qtyCurrent: number; status: VolumeStatus
  openedAt: string | null; createdAt: string
}

export type MaterialMovement = {
  id: string; movedAt: string; type: MovementType; quantity: number
  notes: string | null; referenceType: string | null; referenceId: string | null
  materialCode: string | null; materialName: string | null
  lotNumber: string | null; volumeCode: string | null
  ownerName: string | null; locationCode: string | null; author: string | null
}

export type MaterialDetail = Omit<MaterialRow, 'quantity' | 'belowMin' | 'productSku'> & {
  productId: string | null; productSku: string | null
  reorderQty: number | null; defaultSupplierId?: string | null
  lastCost?: string | null; supplierBarcode: string | null; notes: string | null
  quantity: number; belowMin: boolean
  byOwner: { ownerTenantId: string | null; ownerName: string | null; quantity: number }[]
  lots: MaterialLot[]
  volumes: MaterialVolume[]
  photos: { id: string; kind: string; createdAt: string }[]
  movements: MaterialMovement[]
  // Quem já entregou este material, pelos lotes recebidos. Vazio para quem não
  // pode ver custo.
  suppliers: {
    supplierId: string | null; supplierName: string | null; leadTimeDays: number | null
    lotes: number; recebido: number; ultimoCusto: string | null; ultimaEntrada: string
  }[]
}

export type VolumeSearchRow = {
  id: string; code: string; qtyInitial: number; qtyCurrent: number; status: VolumeStatus
  materialId: string; materialCode: string; materialName: string; unit: MaterialUnit
  lotNumber: string | null; expiryDate: string | null
  ownerName: string | null; locationCode: string | null; createdAt: string
}

export type Location = { id: string; code: string; type: string; zone: string; active: boolean }

/** Etiqueta de volume: uma por página 10x15, ou 4 por folha A4. */
export async function printVolumeLabels(volumeIds: string[], format: 'label' | 'a4' = 'label') {
  const res = await api.post('/material-volumes/labels', { volumeIds, format }, { responseType: 'blob' })
  const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
  const win = window.open(url, '_blank')
  // Bloqueador de pop-up: cai para download, que nunca é bloqueado.
  if (!win) {
    const a = document.createElement('a')
    a.href = url
    a.download = `etiquetas-${volumeIds.length}-volumes.pdf`
    a.click()
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result as string)
    r.onerror = reject
    r.readAsDataURL(file)
  })
}

/** Foto do celular tem 4 MB e 4000 px; o que a etiqueta e a prova de avaria
 *  precisam cabe em 1600 px. Reduz antes de subir — o corpo da requisição tem
 *  teto e dez fotos crus estouram. */
export async function resizeImage(file: File, maxPx = 1600, quality = 0.82): Promise<string> {
  const dataUrl = await readAsDataURL(file)
  if (!file.type.startsWith('image/')) return dataUrl
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image()
      i.onload = () => resolve(i)
      i.onerror = reject
      i.src = dataUrl
    })
    const scale = Math.min(1, maxPx / Math.max(img.width, img.height))
    if (scale === 1 && dataUrl.length < 1_500_000) return dataUrl
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(img.width * scale)
    canvas.height = Math.round(img.height * scale)
    const ctx = canvas.getContext('2d')
    if (!ctx) return dataUrl
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL('image/jpeg', quality)
  } catch {
    return dataUrl   // navegador antigo: sobe como veio
  }
}

/** Mensagem de erro da API (problem+json) — sem isso o usuário vê "Request failed". */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function apiError(e: any, fallback: string): string {
  return e?.response?.data?.title ?? e?.response?.data?.message ?? fallback
}

export const fmtDate = (v: string | null | undefined) =>
  v ? new Date(v.length <= 10 ? `${v}T00:00:00` : v).toLocaleDateString('pt-BR') : '—'

export const fmtDateTime = (v: string | null | undefined) =>
  v ? new Date(v).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—'

export const fmtMoney = (v: string | number | null | undefined) =>
  v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
